import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api/client';
import { profileApi } from '@/lib/api/endpoints';
import { routes } from '@/lib/routes';

type PublicProfile = {
  academiaId: string;
  name: { ru?: string; en?: string };
  city?: string;
  country?: string;
  avatarUrl?: string;
  status?: string;
  degree?: string;
  scienceField?: string;
  interests?: string;
  ids?: Record<string, string>;
  publications?: Array<Record<string, unknown>>;
  education?: Array<Record<string, unknown>>;
  work?: Array<Record<string, unknown>>;
  projects?: Array<Record<string, unknown>>;
};

const PublicProfilePage = () => {
  const { academiaId } = useParams();
  const [data, setData] = useState<PublicProfile | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!academiaId) return;
    setLoading(true);
    void profileApi
      .getPublic(academiaId)
      .then((payload) => {
        setData(payload as PublicProfile);
        setError('');
      })
      .catch((err) => {
        setData(null);
        setError(err instanceof ApiError ? err.message : 'Профиль недоступен');
      })
      .finally(() => setLoading(false));
  }, [academiaId]);

  if (loading) {
    return (
      <div className="public-profile">
        <p>Загрузка публичного профиля…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="public-profile">
        <h1>Профиль недоступен</h1>
        <p>{error || 'Публичный профиль отключён или не найден.'}</p>
        <Link to={routes.home}>На главную</Link>
      </div>
    );
  }

  const displayName = data.name?.ru || data.name?.en || data.academiaId;
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <div className="public-profile">
      <header className="public-profile-hero">
        <div className="public-profile-avatar" aria-hidden="true">
          {data.avatarUrl ? <img src={data.avatarUrl} alt="" /> : initials}
        </div>
        <div>
          <small>{data.academiaId}</small>
          <h1>{displayName}</h1>
          <p>
            {[data.degree, data.status, data.scienceField].filter(Boolean).join(' · ') ||
              'Академический профиль'}
          </p>
          <span>
            {[data.city, data.country].filter(Boolean).join(', ') || 'Локация не указана'}
          </span>
        </div>
      </header>

      {data.ids && Object.keys(data.ids).length ? (
        <section>
          <h2>Научные идентификаторы</h2>
          <ul className="public-profile-ids">
            {Object.entries(data.ids).map(([key, value]) => (
              <li key={key}>
                <b>{key.toUpperCase()}</b>
                <span>{value}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.interests ? (
        <section>
          <h2>Интересы</h2>
          <p>{data.interests}</p>
        </section>
      ) : null}

      {data.publications?.length ? (
        <section>
          <h2>Публикации</h2>
          <ul>
            {data.publications.map((pub, i) => (
              <li key={i}>
                <b>{String(pub.title || 'Без названия')}</b>
                <span>
                  {[pub.journal, pub.year, pub.indexing].filter(Boolean).join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.education?.length ? (
        <section>
          <h2>Образование</h2>
          <ul>
            {data.education.map((ed, i) => (
              <li key={i}>
                {String(ed.institution || ed.level || 'Запись')}
                {ed.program ? ` · ${String(ed.program)}` : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.work?.length ? (
        <section>
          <h2>Место работы</h2>
          <ul>
            {data.work.map((w, i) => (
              <li key={i}>
                {[w.organization, w.position].filter(Boolean).join(' · ')}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};

export default PublicProfilePage;
